import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import api from "@/admin/lib/api";
import { useOrg } from "@/admin/hooks/use-org";
import { PICKER_PAGE_SIZE } from "@/admin/hooks/use-products";
import type { PartnershipOption } from "@/admin/hooks/use-products";
import type { ResourceLookupRegistry } from "@/shared/components/ui/resource-lookup-context";
import type { ResourceOption } from "@/shared/components/ui/resource-search-input";

/** Build resource lookup providers from the current org context.
 *  Each provider key (e.g. "partnership") maps to a search function
 *  that returns ResourceOption[]. New resource types can be added here
 *  without touching any shared component. Shared by the entity action menu
 *  and any other surface that needs a search-as-you-type resource picker
 *  (e.g. the API-keys partner picker). */
export function useResourceLookupProviders(): ResourceLookupRegistry {
  const { orgUuid } = useOrg();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMemo<ResourceLookupRegistry>(
    () => ({
      partnership: {
        // Routed through the React Query cache so re-opening the picker or
        // repeating a term paints from cache (SEARCH policy) instead of
        // re-hitting the server; the cached value is the mapped option list.
        //
        // Optional `params.role_name` scopes the picker to one external role
        // (e.g. "Retailer") by its stable natural key - forwarded to the
        // partnerships endpoint's `role_name` filter. Each option carries the
        // raw partnership row as `data` so a consumer can pre-fill sibling
        // fields from it via an ENTITY_SEARCH `prefill_map`.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const roleName =
            typeof params?.role_name === "string"
              ? params.role_name
              : undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "partnership",
              orgUuid,
              roleName ?? "",
              term,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<
                { results?: PartnershipOption[] } | PartnershipOption[]
              >(`/organisations/${orgUuid}/partnerships/`, {
                params: {
                  search: term,
                  include_locations: "true",
                  // Suspended partners are excluded in SQL, not here: the
                  // response is one page, so filtering after it let suspended
                  // rows eat the page and hid active dealers that sorted past
                  // it - with no "load more" to reveal them.
                  is_active: "true",
                  page_size: String(PICKER_PAGE_SIZE),
                  ...(roleName ? { role_name: roleName } : {}),
                },
              });
              const results: PartnershipOption[] = Array.isArray(data)
                ? data
                : (data.results ?? []);
              return results
                .sort((a, b) => {
                  const aHq = (a.child_partnership_count ?? 0) > 0 ? 0 : 1;
                  const bHq = (b.child_partnership_count ?? 0) > 0 ? 0 : 1;
                  return aHq - bHq;
                })
                .map((p) => ({
                  uuid: p.uuid,
                  name: p.downstream_organisation?.name ?? "",
                  secondary: p.attribute_summary
                    ? Object.values(p.attribute_summary)
                        .filter(
                          (v): v is string =>
                            typeof v === "string" && v.trim().length > 0,
                        )
                        .join(" · ")
                    : undefined,
                  isParent: (p.child_partnership_count ?? 0) > 0,
                  data: p as unknown as Record<string, unknown>,
                }));
            },
          });
        },
      },
      product_variant: {
        // `params.category_slug` (forwarded by an ENTITY_SEARCH field's
        // `scope_param`/`scope_map` cascade) scopes the search to one category
        // subtree - e.g. a "Product Impacted = Shingle" selection narrows the
        // picker to the shingle-lines catalogue instead of the whole catalogue.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const categorySlug =
            typeof params?.category_slug === "string" && params.category_slug
              ? params.category_slug
              : undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "product_variant",
              term,
              categorySlug ?? null,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: {
                  uuid: string;
                  cached_short_name: string;
                  cached_long_name: string;
                  product_type: string;
                  product_data?: Record<string, unknown>;
                  category?: { name: string } | null;
                  parent_product?: { cached_short_name: string } | null;
                }[];
              }>("/products/items/", {
                params: {
                  search: term,
                  status: "PUBLISHED",
                  exclude_variants: "false",
                  page_size: "20",
                  ...(categorySlug ? { category_slug: categorySlug } : {}),
                },
              });
              return (data.results ?? [])
                .filter((p) => p.product_type === "VARIANT")
                .map((p) => {
                  const pd = p.product_data ?? {};
                  const str = (v: unknown): string =>
                    typeof v === "string"
                      ? v.trim()
                      : typeof v === "number"
                        ? String(v)
                        : "";
                  const sku = str(pd.sku) || str(pd.SKU);
                  // Disambiguate which product / type / size this variant is
                  // (UAT CLM-010/012/013). Every value is read from generic
                  // product metadata - no tenant-specific keys - so any catalogue
                  // that populates them benefits. `parent_product` already carries
                  // the brand + sub-brand + line for catalogues that model variants
                  // under a base product; `sub_brand` / `size_label` cover
                  // catalogues that keep those on the variant's own metadata.
                  const line =
                    p.parent_product?.cached_short_name || str(pd.product_line);
                  const subBrand = str(pd.sub_brand);
                  // Prefer an explicit size label, else compose one from
                  // diameter/length when present (e.g. garden hoses).
                  const size =
                    str(pd.size_label) ||
                    [str(pd.diameter_in), str(pd.length_ft)]
                      .filter(Boolean)
                      .join(" x ");
                  const secondary = [
                    line,
                    // Only add sub-brand when the line label doesn't already
                    // include it, so we don't repeat it twice.
                    subBrand && !(line && line.includes(subBrand))
                      ? subBrand
                      : "",
                    size,
                    p.category?.name,
                    sku ? `SKU: ${sku}` : "",
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return {
                    uuid: p.uuid,
                    name: p.cached_short_name || p.cached_long_name,
                    secondary: secondary || undefined,
                  };
                });
            },
          });
        },
      },
      product_model: {
        // Product *models* (VARIANT_BASE) - "TruDefinition Duration",
        // "Oakridge PRO 50", "Berkshire" - as distinct from `product_variant`
        // above, which is the SKU/colour row beneath a model.
        //
        // Job Stop needs the model rather than the variant: a contractor stops
        // a job because of what is in their stock, so they name the product off
        // the bundle. There is no registration on that claim type to read it
        // from, and the colour/SKU is a separate question.
        // Takes the same optional `category_slug` scope as `product_variant`.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const categorySlug =
            typeof params?.category_slug === "string" && params.category_slug
              ? params.category_slug
              : undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "product_model",
              term,
              categorySlug ?? null,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: {
                  uuid: string;
                  cached_short_name: string;
                  cached_long_name: string;
                  product_type: string;
                  category?: { name: string } | null;
                }[];
              }>("/products/items/", {
                params: {
                  search: term,
                  status: "PUBLISHED",
                  exclude_variants: "true",
                  page_size: "20",
                  ...(categorySlug ? { category_slug: categorySlug } : {}),
                },
              });
              return (data.results ?? [])
                .filter((p) => p.product_type === "VARIANT_BASE")
                .map((p) => ({
                  uuid: p.uuid,
                  name: p.cached_short_name || p.cached_long_name,
                  secondary: p.category?.name || undefined,
                }));
            },
          });
        },
      },
      plant_service_area: {
        // WPS-151 retest 2A: plants (Plant Service Area zones) for the claim
        // "Plant Code" picker, so the lab selects a plant by NAME ("Summit")
        // rather than typing a raw plant number. The name IS the stored/displayed
        // value; the plant code rides along as the secondary line. Served by the
        // lab-accessible read-only endpoint (not the admin geo-zones CRUD).
        search: (term: string): Promise<ResourceOption[]> =>
          queryClient.fetchQuery({
            queryKey: ["resource-search", "plant_service_area", term],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: { uuid: string; name: string; code?: string }[];
              }>("/geo/plant-areas/", {
                params: { search: term, page_size: "20" },
              });
              return (data.results ?? []).map((z) => ({
                uuid: z.uuid,
                name: z.name,
                secondary: z.code || undefined,
              }));
            },
          }),
      },
      product_colour: {
        // Colours of the tenant's published variants - "Estate Gray",
        // "Driftwood", "Onyx Black". A real catalogue value, never typed
        // prose, so a new colourway is offered the day it ships. Colours are
        // not term-searchable server-side, so fetch once (cached) and filter
        // client-side, as `product_line` above does.
        //
        // `params.parent_uuid` (a `depends_on` + `scope_param` cascade from the
        // product picker) narrows the list to that product's own colours;
        // without it, the whole palette.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const parentUuid =
            typeof params?.parent_uuid === "string" && params.parent_uuid
              ? params.parent_uuid
              : undefined;
          return queryClient
            .fetchQuery({
              queryKey: [
                "resource-search",
                "product_colour",
                orgUuid,
                parentUuid ?? null,
              ],
              queryFn: async (): Promise<ResourceOption[]> => {
                const { data } = await api.get<{
                  results?: { product_data?: Record<string, unknown> }[];
                }>(
                  parentUuid
                    ? `/products/items/${parentUuid}/variants/`
                    : "/products/items/",
                  {
                    params: parentUuid
                      ? { status: "PUBLISHED", page_size: "500" }
                      : {
                          status: "PUBLISHED",
                          exclude_variants: "false",
                          page_size: "500",
                        },
                  },
                );
                const seen = new Set<string>();
                for (const v of data.results ?? []) {
                  const colour = (v.product_data ?? {}).color;
                  if (typeof colour === "string" && colour.trim())
                    seen.add(colour.trim());
                }
                return Array.from(seen)
                  .sort()
                  .map((c) => ({ uuid: c, name: c }));
              },
            })
            .then((options) => {
              const q = term.trim().toLowerCase();
              return q
                ? options.filter((o) => o.name.toLowerCase().includes(q))
                : options;
            });
        },
      },
      attribute_template: {
        // Backs the "select an existing attribute or create a new one"
        // picker shown wherever an attribute definition is authored (Entity
        // Editor, product/host/transaction templates, warranty classes,
        // claim schemas, workflow attributes). `data` carries the full
        // template row so a consumer can prefill display_name/code_name/
        // field_type/configuration_data and lock the type + options editor.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          // `domain` scopes results to the calling surface (e.g. "product"),
          // so a product template's picker does not surface claim/workflow
          // attributes. Omitted -> the whole tenant bucket.
          const domain =
            typeof params?.domain === "string" ? params.domain : undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "attribute_template",
              domain ?? "",
              term,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: {
                  uuid: string;
                  code_name: string;
                  display_name: string;
                  description?: string;
                  field_type: string;
                  hint?: string;
                  configuration_data?: Record<string, unknown>;
                  category?: string;
                  is_suggested?: boolean;
                }[];
              }>("/attribute-templates/", {
                // Load the full tenant bucket (max_page_size ceiling) so the
                // picker can browse every shared attribute, not just page 1.
                params: {
                  search: term,
                  page_size: 1000,
                  ...(domain ? { domain } : {}),
                },
              });
              return (data.results ?? []).map((r) => ({
                uuid: r.uuid,
                name: r.display_name,
                secondary: r.code_name,
                badge: r.field_type,
                data: r as unknown as Record<string, unknown>,
              }));
            },
          });
        },
      },
      product_line: {
        // The tenant's existing pricing product lines (union of the labor +
        // material rate facets). `product_line` is a free-text code, so pair
        // this provider with `allow_create` on the field to still let an admin
        // enter a brand-new line. Facets are not term-searchable server-side,
        // so we fetch the full list once (cached) and filter client-side.
        search: (term: string): Promise<ResourceOption[]> =>
          queryClient
            .fetchQuery({
              queryKey: ["resource-search", "product_line", orgUuid],
              queryFn: async (): Promise<ResourceOption[]> => {
                const load = (path: string) =>
                  api
                    .get<{ product_lines?: string[] }>(path)
                    .then((r) => r.data.product_lines ?? [])
                    .catch(() => [] as string[]);
                const [labor, material] = await Promise.all([
                  load("/claims/pricing/labor-rates/facets/"),
                  load("/claims/pricing/material-rates/facets/"),
                ]);
                return Array.from(new Set([...labor, ...material]))
                  .filter((line) => line.trim().length > 0)
                  .sort()
                  .map((line) => ({ uuid: line, name: line }));
              },
            })
            .then((options) => {
              const q = term.trim().toLowerCase();
              return q
                ? options.filter((o) => o.name.toLowerCase().includes(q))
                : options;
            }),
      },
      entity_record: {
        // Records of a pure-data (Tier 2) entity - e.g. the vendor directory a
        // tenant loads from its ERP. `params.entity_key` names the entity;
        // `params.secondary` lists the record values shown under each name
        // (an address reads as its lines); `params.meta` names one value shown
        // right-aligned beside it (an account number). `params.name_case:
        // "upper"` shows every name in one case, for directories whose source
        // mixes them. `params.min_search_length` holds the list back until that
        // many characters are typed - a directory of thousands is no use
        // browsed from the top. Results come best match first. Each option carries the record as
        // `data`, so an ENTITY_SEARCH `prefill_map` can fill sibling fields
        // from `values.<code>`. Searched server-side on the indexed projection
        // text, one page at a time.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const entityKey =
            typeof params?.entity_key === "string" ? params.entity_key : "";
          const minLength =
            typeof params?.min_search_length === "number"
              ? params.min_search_length
              : 0;
          if (!entityKey || term.trim().length < minLength) {
            return Promise.resolve([]);
          }
          const secondary = Array.isArray(params?.secondary)
            ? params.secondary.filter(
                (code): code is string => typeof code === "string",
              )
            : [];
          const meta = typeof params?.meta === "string" ? params.meta : "";
          const upper = params?.name_case === "upper";
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "entity_record",
              orgUuid,
              entityKey,
              term,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: EntityRecordOption[];
              }>(`/entities/types/${entityKey}/records/search/`, {
                params: {
                  search: term.trim(),
                  ordering: "relevance",
                  page_size: String(PICKER_PAGE_SIZE),
                },
              });
              return (data.results ?? []).map((record) => ({
                uuid: record.record_uuid,
                name: upper ? record.label.toUpperCase() : record.label,
                secondary:
                  secondary
                    .map((code) => recordValueText(record.values?.[code]))
                    .filter(Boolean)
                    .join(" · ") || undefined,
                meta: meta
                  ? recordValueText(record.values?.[meta]) || undefined
                  : undefined,
                data: record as unknown as Record<string, unknown>,
              }));
            },
          });
        },
      },
      claim_attribute_history: {
        // Free-text type-ahead over the distinct historical values of a claim
        // attribute (WPS-164 #15: Plant / Production Code has no lookup table -
        // SAP-sourced, like suppliers). `params.code_name` names the attribute
        // (the field renderer passes its own code_name); each past value
        // becomes an option whose `name` IS the value (empty uuid - the stored
        // value is plain text, not a resource reference). Generic: any TEXT
        // attribute with `autocomplete_source: "claim_attribute_history"` uses
        // it, no attribute is named here.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const codeName =
            typeof params?.code_name === "string" ? params.code_name : "";
          if (!codeName) return Promise.resolve([]);
          return api
            .get<{ result?: { values?: string[] } }>(
              "/claims/attribute-history/",
              { params: { code_name: codeName, q: term } },
            )
            .then((r) =>
              (r.data.result?.values ?? []).map((v) => ({ uuid: "", name: v })),
            )
            .catch(() => []);
        },
      },
      value_stock: {
        // Values loaded for this attribute and scope that are still free.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const attribute =
            typeof params?.attribute === "string" ? params.attribute : "";
          const entity =
            typeof params?.entity === "string" ? params.entity : "holding";
          const scopeEntity =
            typeof params?.scope_entity === "string"
              ? params.scope_entity
              : "product";
          const scopeId =
            typeof params?.scope_id === "string" ? params.scope_id : "";
          const status =
            typeof params?.status === "string" ? params.status : "available";
          if (!attribute || !scopeId) return Promise.resolve([]);
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "value_stock",
              orgUuid,
              entity,
              attribute,
              scopeEntity,
              scopeId,
              status,
              term,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<{
                results?: { value: string }[];
              }>("/value-stock/values/", {
                params: {
                  entity,
                  attribute,
                  scope_entity: scopeEntity,
                  scope_id: scopeId,
                  status,
                  query: term || undefined,
                  page_size: "20",
                },
              });
              return (data.results ?? []).map((row) => ({
                uuid: row.value,
                name: row.value,
              }));
            },
          });
        },
      },
      inspector: {
        // Backs the INSPECTOR capture field on the inspection workflow's
        // schedule/reschedule actions. Searches the tenant's inspector-role
        // users (`GET /inspections/inspectors/`) so an admin picks a real
        // User row instead of typing a raw UUID. `provider_params.inspector_type`
        // scopes the list (e.g. "internal" / "external") when set on the field.
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const inspectorType =
            typeof params?.inspector_type === "string"
              ? params.inspector_type
              : undefined;
          return queryClient.fetchQuery({
            queryKey: [
              "resource-search",
              "inspector",
              orgUuid,
              inspectorType ?? "",
              term,
            ],
            queryFn: async (): Promise<ResourceOption[]> => {
              const { data } = await api.get<
                {
                  uuid: string;
                  email: string;
                  full_name?: string;
                  role?: string;
                  inspector_type?: string;
                }[]
              >("/inspections/inspectors/", {
                params: {
                  search: term,
                  ...(inspectorType ? { inspector_type: inspectorType } : {}),
                },
              });
              return (data ?? []).map((r) => ({
                uuid: r.uuid,
                name: r.full_name?.trim() || r.email,
                secondary: r.full_name?.trim() ? r.email : undefined,
                badge: r.inspector_type || r.role || undefined,
              }));
            },
          });
        },
      },
      issuable_warranty: {
        // The plans staff may issue on the registration an action runs on
        // (`params.resource_uuid`, passed by the action menu). One request per
        // registration; the typed term filters it here. The default plan is
        // marked so the field starts on it, and each option carries the start
        // date it would be issued with (`data.active_from`).
        search: (
          term: string,
          params?: Record<string, unknown>,
        ): Promise<ResourceOption[]> => {
          const uuid =
            typeof params?.resource_uuid === "string" ? params.resource_uuid : "";
          if (!uuid) return Promise.resolve([]);
          return queryClient
            .fetchQuery({
              queryKey: ["resource-search", "issuable_warranty", orgUuid, uuid],
              queryFn: async () => {
                const { data } = await api.get<IssuableWarranties>(
                  `/registrations/${uuid}/issuable-warranties/`,
                );
                return data;
              },
            })
            .then(({ active_from, options }) => {
              const needle = term.trim().toLowerCase();
              return options
                .filter((o) =>
                  `${o.name} ${o.template_name}`.toLowerCase().includes(needle),
                )
                .map((o) => ({
                  uuid: o.uuid,
                  name: o.name,
                  // Option names often already lead with their template's.
                  secondary: o.name.includes(o.template_name)
                    ? undefined
                    : o.template_name,
                  badge: o.is_default
                    ? t("registrations.issueWarranty.defaultPlan")
                    : undefined,
                  isDefault: o.is_default,
                  data: { active_from },
                }));
            });
        },
      },
    }),
    [orgUuid, queryClient, t],
  );
}

/** `GET /registrations/<uuid>/issuable-warranties/`. */
interface IssuableWarranties {
  active_from: string;
  options: {
    uuid: string;
    name: string;
    template_name: string;
    is_default: boolean;
  }[];
}

/** One row of `/entities/types/<key>/records/search/`. */
interface EntityRecordOption {
  record_uuid: string;
  label: string;
  values?: Record<string, unknown>;
}

/** The address envelope's parts, in reading order. */
const ADDRESS_PARTS = [
  "addressLine1",
  "addressLine2",
  "locality",
  "region",
  "postalCode",
] as const;

/**
 * An address part as it reads in a list. ERP exports often hold addresses in
 * capitals; those read in normal case ("1817 Aston Ave"), while a short code
 * such as a state ("WY") stays as it is. Mixed-case text is left alone.
 */
function readableAddressPart(text: string, part: string): string {
  const trimmed = text.trim();
  if (part === "region" || part === "postalCode" || /[a-z]/.test(trimmed)) {
    return trimmed;
  }
  return trimmed
    .toLowerCase()
    .replace(/(^|[\s/-])([a-z])/g, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/** A record value as one line of text: an address reads as its lines. */
function recordValueText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    return ADDRESS_PARTS.map((part) => {
      const text = record[part];
      return typeof text === "string" && text.trim()
        ? readableAddressPart(text, part)
        : "";
    })
      .filter(Boolean)
      .join(", ");
  }
  return "";
}
